/**
 * React Query hooks for Change Request data
 * Implements 4-eye principle approval workflow
 */

import { queryKeys, useApiQuery, useApiMutation } from './index';
import { useQueryClient } from '@tanstack/react-query';
import { changeRequestService } from '@/services/changeRequestService';
import type {
  ChangeRequestCreate,
  ChangeRequestCreateResponse,
  ChangeRequestDetail,
} from '@/types/changeRequest';

// Admin hooks

/**
 * Hook for fetching all change requests for a specific investor (admin)
 */
export function useInvestorChangeRequests(investorId: string) {
  return useApiQuery(
    queryKeys.changeRequests.investor(investorId),
    () => changeRequestService.getInvestorRequests(investorId),
    {
      enabled: !!investorId,
      staleTime: 60 * 1000,
    }
  );
}

/**
 * Hook for fetching a single change request (admin)
 */
export function useChangeRequest(requestId: string) {
  return useApiQuery(
    queryKeys.changeRequests.detail(requestId),
    () => changeRequestService.getChangeRequest(requestId),
    {
      enabled: !!requestId,
      staleTime: 30 * 1000, // 30 seconds
    }
  );
}

/**
 * Hook for approving a change request (admin)
 */
export function useApproveChangeRequest() {
  const queryClient = useQueryClient();

  return useApiMutation<
    ChangeRequestDetail,
    Error,
    { requestId: string; reviewerNotes?: string }
  >(
    ({ requestId, reviewerNotes }) =>
      changeRequestService.approveChangeRequest(requestId, reviewerNotes),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.changeRequests.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.changeRequests.detail(variables.requestId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.investors.all,
        });
      },
    }
  );
}

/**
 * Hook for rejecting a change request (admin)
 */
export function useRejectChangeRequest() {
  const queryClient = useQueryClient();

  return useApiMutation<
    ChangeRequestDetail,
    Error,
    { requestId: string; rejectionReason: string; reviewerNotes?: string }
  >(
    ({ requestId, rejectionReason, reviewerNotes }) =>
      changeRequestService.rejectChangeRequest(
        requestId,
        rejectionReason,
        reviewerNotes
      ),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.changeRequests.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.changeRequests.detail(variables.requestId),
        });
      },
    }
  );
}

// Portal hooks (investor contacts)

/**
 * Hook for fetching my change requests (investor portal)
 */
export function useMyChangeRequests() {
  return useApiQuery(
    queryKeys.changeRequests.myRequests(),
    () => changeRequestService.getMyRequests(),
    {
      staleTime: 0,
    }
  );
}

/**
 * Hook for creating a change request (investor portal)
 */
export function useCreateChangeRequest() {
  const queryClient = useQueryClient();

  return useApiMutation<
    ChangeRequestCreateResponse,
    Error,
    ChangeRequestCreate
  >((data) => changeRequestService.createChangeRequest(data), {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.changeRequests.myRequests(),
      });
    },
  });
}

/**
 * Hook for cancelling a change request (investor portal)
 */
export function useCancelChangeRequest() {
  const queryClient = useQueryClient();

  return useApiMutation<void, Error, string>(
    (requestId) => changeRequestService.cancelChangeRequest(requestId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.changeRequests.myRequests(),
        });
      },
    }
  );
}
